import { Check, FileText, Loader2, Mail, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { SetupStatus } from './setup-api';

const PHASES = ['emails', 'documents', 'summarizing'] as const;

export function ImportProgress({ status }: { status: SetupStatus }) {
  const { t } = useTranslation('onboarding');
  const run = status.run;
  const current = PHASES.indexOf((run?.phase ?? 'emails') as (typeof PHASES)[number]);
  // מסמכים שהועלו (בלי Google): אין שלב מיילים.
  const fromUploads = run?.source === 'DOCUMENTS';
  const customers = status.suggestions
    .filter((s) => s.kind === 'CUSTOMER')
    .reduce((sum, s) => sum + s.count, 0);

  return (
    <Card>
      <CardContent className="space-y-6 py-8">
        <div className="text-center" aria-live="polite">
          <h2 className="text-base font-semibold text-fg">{t('progress.title')}</h2>
          <p className="mt-1 text-sm text-fg-muted">{t('progress.hint')}</p>
        </div>
        <ol className="mx-auto max-w-sm space-y-3">
          {fromUploads ? null : (
            <Phase
              index={0}
              current={current}
              icon={<Mail aria-hidden />}
              label={t('progress.readingMail')}
              detail={
                run?.emailsScanned
                  ? `${t('progress.emails', { count: run.emailsScanned })}${customers ? ` · ${t('progress.customers', { count: customers })}` : ''}`
                  : undefined
              }
            />
          )}
          <Phase
            index={1}
            current={current}
            icon={<FileText aria-hidden />}
            label={t('progress.readingDocs')}
            detail={
              run?.docsParsed ? t('progress.documents', { count: run.docsParsed }) : undefined
            }
          />
          <Phase
            index={2}
            current={current}
            icon={<Sparkles aria-hidden />}
            label={t('progress.building')}
          />
        </ol>
      </CardContent>
    </Card>
  );
}

function Phase({
  index,
  current,
  icon,
  label,
  detail,
}: {
  index: number;
  current: number;
  icon: ReactNode;
  label: string;
  detail?: string;
}) {
  const done = index < current;
  const active = index === current;
  return (
    <li className={cn('flex items-center gap-3 text-sm', !done && !active && 'text-fg-subtle')}>
      <span
        className={cn(
          'grid size-8 shrink-0 place-items-center rounded-full border [&_svg]:size-4',
          done && 'border-success-border bg-success-subtle text-success',
          active && 'border-accent-border bg-accent-subtle text-accent',
          !done && !active && 'border-border',
        )}
      >
        {done ? (
          <Check aria-hidden />
        ) : active ? (
          <Loader2 className="animate-spin" aria-hidden />
        ) : (
          icon
        )}
      </span>
      <span className="flex-1">
        <span className={cn(active && 'font-medium text-fg')}>{label}</span>
        {detail ? <span className="block text-xs text-fg-muted">{detail}</span> : null}
      </span>
    </li>
  );
}
